/**
 * ComparisonRenderer — comparison
 * Side-by-side left vs right comparison with optional badges and bullets.
 */
import React from "react";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { Title } from "../../ui/Title";
import { Text } from "../../ui/Text";
import { Eyebrow } from "../../ui/Eyebrow";
import { BulletListUI } from "../../ui/BulletListUI";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional, resolveTextArray } from "../../utils/resolveText";

/* eslint-disable react/prop-types */

function ComparisonColumn({
  col,
  colIndex,
  accent,
  editing,
  onChange,
  onBulletsChange,
  onDelete,
  playing,
  isPaused,
  currentTimeSec,
}) {
  if (!col) return null;
  const title = resolveText(col.title);
  const body = resolveTextOptional(col.body);
  const bullets = col.bullets ? resolveTextArray(col.bullets) : null;
  const badge = resolveTextOptional(col.badge);
  const colBox = col.boxAnimation ?? null;
  const colExit = col.boxExitAnimation ?? null;

  return (
    <AnimatedField
      boxAnimation={colBox}
      boxExitAnimation={colExit}
      playing={playing}
      isPaused={isPaused}
      currentTimeSec={currentTimeSec}
    >
      <div
        style={{
          display: "grid",
          gap: "var(--space-3)",
          padding: "var(--surface-padding-compact)",
          background: accent ? "var(--color-bg-surface-strong)" : "var(--color-bg-surface)",
          borderRadius: "var(--surface-radius)",
          border: "var(--stroke-thin) solid var(--color-border-subtle)",
          alignContent: "start",
          position: "relative",
        }}
      >
        {editing && onDelete && (
          <button
            onClick={onDelete}
            style={{
              position: "absolute",
              top: "var(--space-2)",
              right: "var(--space-2)",
              background: "var(--color-bg-surface-strong)",
              border: "var(--stroke-thin) solid var(--color-border-subtle)",
              borderRadius: "var(--radius-sm)",
              color: "var(--color-text-muted)",
              cursor: "pointer",
              fontSize: "var(--text-xs)",
              padding: "2px 8px",
              lineHeight: 1.4,
              zIndex: 1,
            }}
            title="Delete option"
          >
            ✕
          </button>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Title size="lg">
            <EditableText
              value={title}
              editing={editing}
              onChange={(val) => onChange(`columns.${colIndex}.title`, val)}
            />
          </Title>
          {badge && (
            <span
              style={{
                padding: "2px 10px",
                borderRadius: "var(--radius-full)",
                background: "var(--color-bg-accent)",
                color: "var(--color-text-primary)",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
              }}
            >
              {badge}
            </span>
          )}
        </div>
        {(body || editing) && (
          <Text role="body">
            <EditableText
              value={body || ""}
              editing={editing}
              onChange={(val) => onChange(`columns.${colIndex}.body`, val)}
            />
          </Text>
        )}
        {bullets && !editing && <BulletListUI items={bullets} />}
        {editing && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {(bullets || []).map((item, bi) => (
              <div
                key={bi}
                style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start" }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    marginTop: "0.35em",
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: "var(--color-text-accent)",
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: "var(--text-sm)",
                    color: "var(--color-text-primary)",
                    fontFamily: "var(--font-body)",
                    lineHeight: 1.6,
                  }}
                >
                  <EditableText
                    value={item}
                    editing={editing}
                    onChange={(val) => onChange(`columns.${colIndex}.bullets.${bi}`, val)}
                  />
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const newBullets = (col.bullets || []).filter((_, idx) => idx !== bi);
                    onBulletsChange(newBullets);
                  }}
                  style={{
                    flexShrink: 0,
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    borderRadius: "var(--radius-sm, 4px)",
                    background: "transparent",
                    color: "var(--color-text-muted, #94a3b8)",
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onBulletsChange([...(col.bullets || []), "New item"])}
              style={{
                width: "100%",
                padding: "4px",
                border: "1px dashed var(--color-border-subtle, rgba(255,255,255,0.12))",
                borderRadius: "var(--radius-sm, 4px)",
                background: "transparent",
                color: "var(--color-text-muted, #94a3b8)",
                cursor: "pointer",
                fontSize: "var(--text-sm, 13px)",
                fontFamily: "var(--font-body, sans-serif)",
                textAlign: "center",
              }}
            >
              + Add item
            </button>
          </div>
        )}
      </div>
    </AnimatedField>
  );
}

export function ComparisonRenderer({
  element,
  editing,
  onChange,
  onReorder,
  playing,
  isPaused,
  currentTimeSec,
}) {
  const heading = element.heading ? resolveText(element.heading) : null;
  const eyebrow = element.eyebrow ? resolveText(element.eyebrow) : null;

  // Support element.columns array or fall back to element.left / element.right
  const columns = element.columns || [element.left, element.right].filter(Boolean);

  const handleDelete = (index) => {
    const next = columns.filter((_, i) => i !== index);
    onReorder("columns", next);
  };

  const handleAdd = () => {
    onReorder("columns", [...columns, { title: "New Option", body: "" }]);
  };

  const gridCols = columns.length > 0 ? `repeat(${columns.length}, 1fr)` : "1fr 1fr";

  return (
    <SurfaceCard>
      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        {heading && <Title size="2xl">{heading}</Title>}

        <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: "var(--space-4)" }}>
          {columns.map((col, i) => (
            <ComparisonColumn
              key={i}
              col={col}
              colIndex={i}
              accent={i === columns.length - 1}
              editing={editing}
              onChange={onChange}
              onBulletsChange={(newBullets) => {
                const newCols = columns.map((c, idx) =>
                  idx === i ? { ...c, bullets: newBullets } : c
                );
                onReorder("columns", newCols);
              }}
              onDelete={columns.length > 1 ? () => handleDelete(i) : null}
              playing={playing}
              isPaused={isPaused}
              currentTimeSec={currentTimeSec}
            />
          ))}
        </div>

        {editing && (
          <button
            onClick={handleAdd}
            style={{
              alignSelf: "start",
              justifySelf: "start",
              background: "none",
              border: "var(--stroke-thin) dashed var(--color-border-subtle)",
              borderRadius: "var(--radius-sm)",
              color: "var(--color-text-muted)",
              cursor: "pointer",
              fontSize: "var(--text-sm)",
              padding: "6px 14px",
            }}
          >
            + Add option
          </button>
        )}
      </div>
    </SurfaceCard>
  );
}

export default ComparisonRenderer;
